export type ClassificationSubOption = {
  value: string;
  label: string;
};

export type ClassificationGroup = {
  value: string;
  label: string;
  subOptions: ClassificationSubOption[];
};

export type ClassificationValue = {
  main?: string;
  sub?: string[];
};

export const CLASSIFICATION_OPTIONS: ClassificationGroup[] = [
  {
    value: "software & development",
    label: "Software & Development",
    subOptions: [
      { value: "web development", label: "Web Development" },
      { value: "mobile app development", label: "Mobile App Development" },
      { value: "backend development", label: "Backend Development" },
      { value: "full stack development", label: "Full Stack Development" },
    ],
  },
  {
    value: "infrastructure & cloud",
    label: "Infrastructure & Cloud",
    subOptions: [
      { value: "cloud engineering", label: "Cloud Engineering" },
      {
        value: "devops / site reliability engineering (sre)",
        label: "DevOps / Site Reliability Engineering (SRE)",
      },
      {
        value: "system administration / it infrastructure",
        label: "System Administration / IT Infrastructure",
      },
    ],
  },
  {
    value: "security",
    label: "Security",
    subOptions: [
      { value: "cyber security", label: "Cyber Security" },
      {
        value: "information security / grc (governance, risk, compliance)",
        label: "Information Security / GRC (Governance, Risk, Compliance)",
      },
    ],
  },
  {
    value: "data & ai",
    label: "Data & AI",
    subOptions: [
      { value: "data engineering", label: "Data Engineering" },
      {
        value: "data science & analytics",
        label: "Data Science & Analytics",
      },
      { value: "machine learning / ai", label: "Machine Learning / AI" },
      {
        value: "business intelligence (bi)",
        label: "Business Intelligence (BI)",
      },
    ],
  },
  {
    value: "quality & delivery",
    label: "Quality & Delivery",
    subOptions: [
      { value: "qa / test engineering", label: "QA / Test Engineering" },
      { value: "automation testing", label: "Automation Testing" },
    ],
  },
  {
    value: "product & design",
    label: "Product & Design",
    subOptions: [
      { value: "product management", label: "Product Management" },
      { value: "ui/ux design", label: "UI/UX Design" },
      { value: "graphic / visual design", label: "Graphic / Visual Design" },
    ],
  },
  {
    value: "business & operations",
    label: "Business & Operations",
    subOptions: [
      { value: "business analysis", label: "Business Analysis" },
      {
        value: "project / program management",
        label: "Project / Program Management",
      },
      {
        value: "operations / process management",
        label: "Operations / Process Management",
      },
    ],
  },
  {
    value: "support & services",
    label: "Support & Services",
    subOptions: [
      {
        value: "technical support / helpdesk",
        label: "Technical Support / Helpdesk",
      },
      {
        value: "customer success / implementation",
        label: "Customer Success / Implementation",
      },
    ],
  },
  {
    value: "specialized / emerging fields",
    label: "Specialized / Emerging Fields",
    subOptions: [
      { value: "blockchain / web3", label: "Blockchain / Web3" },
      { value: "ar/vr development", label: "AR/VR Development" },
      { value: "embedded systems / iot", label: "Embedded Systems / IoT" },
      { value: "game development", label: "Game Development" },
      { value: "robotics / automation", label: "Robotics / Automation" },
    ],
  },
  {
    value: "engineering & manufacturing",
    label: "Engineering & Manufacturing",
    subOptions: [
      { value: "engineering design / cad", label: "Engineering Design / CAD" },
      { value: "electrical / power engineering", label: "Electrical / Power Engineering" },
      { value: "industrial automation / control systems", label: "Industrial Automation / Control Systems" },
      { value: "manufacturing / production", label: "Manufacturing / Production" },
      { value: "process engineering", label: "Process Engineering" },
      { value: "maintenance / reliability engineering", label: "Maintenance / Reliability Engineering" },
      { value: "quality / inspection", label: "Quality / Inspection" },
      { value: "robotics / mechatronics", label: "Robotics / Mechatronics" },
      { value: "industrial iot / smart manufacturing", label: "Industrial IoT / Smart Manufacturing" },
      { value: "energy / utilities engineering", label: "Energy / Utilities Engineering" },
      { value: "supply chain / logistics", label: "Supply Chain / Logistics" }
    ]
  },

  {
    value: "healthcare & life sciences",
    label: "Healthcare & Life Sciences",
    subOptions: [
      { value: "healthcare / medical", label: "Healthcare / Medical" },
      { value: "clinical systems", label: "Clinical Systems" },
      { value: "medical devices", label: "Medical Devices" },
      { value: "pharmaceuticals", label: "Pharmaceuticals" },
      { value: "biotechnology", label: "Biotechnology" },
      { value: "life sciences / research", label: "Life Sciences / Research" },
      { value: "laboratory systems", label: "Laboratory Systems" }
    ]
  },

  {
    value: "education & training",
    label: "Education & Training",
    subOptions: [
      { value: "education / training", label: "Education / Training" },
      { value: "learning management systems", label: "Learning Management Systems" },
      { value: "corporate training", label: "Corporate Training" },
      { value: "technical training", label: "Technical Training" },
      { value: "e-learning / instructional design", label: "E-Learning / Instructional Design" }
    ]
  },

  {
    value: "legal & compliance",
    label: "Legal & Compliance",
    subOptions: [
      { value: "legal / compliance", label: "Legal / Compliance" },
      { value: "regulatory compliance", label: "Regulatory Compliance" },
      { value: "risk management", label: "Risk Management" },
      { value: "governance / audit", label: "Governance / Audit" },
      { value: "contracts / legal operations", label: "Contracts / Legal Operations" },
      { value: "environment / health / safety", label: "Environment, Health & Safety" }
    ]
  },

  {
    value: "transportation & logistics",
    label: "Transportation & Logistics",
    subOptions: [
      { value: "transportation / logistics", label: "Transportation / Logistics" },
      { value: "fleet management", label: "Fleet Management" },
      { value: "warehouse / distribution", label: "Warehouse / Distribution" },
      { value: "freight / shipping", label: "Freight / Shipping" },
      { value: "rail / transit", label: "Rail / Transit" },
      { value: "automotive / mobility", label: "Automotive / Mobility" },
      { value: "aviation / aerospace", label: "Aviation / Aerospace" },
      { value: "maritime / marine", label: "Maritime / Marine" }
    ]
  },

  {
    value: "agriculture & natural resources",
    label: "Agriculture & Natural Resources",
    subOptions: [
      { value: "agriculture / natural resources", label: "Agriculture / Natural Resources" },
      { value: "precision agriculture", label: "Precision Agriculture" },
      { value: "forestry", label: "Forestry" },
      { value: "mining / minerals", label: "Mining / Minerals" },
      { value: "oil / gas", label: "Oil / Gas" },
      { value: "water / wastewater", label: "Water / Wastewater" },
      { value: "environmental management", label: "Environmental Management" }
    ]
  },
  {
    value: "non-tech",
    label: "Non-Tech (Optional, if platform supports)",
    subOptions: [
      {
        value: "sales / business development",
        label: "Sales / Business Development",
      },
      {
        value: "marketing / digital marketing",
        label: "Marketing / Digital Marketing",
      },
      { value: "finance / accounting", label: "Finance / Accounting" },
      { value: "hr / talent acquisition", label: "HR / Talent Acquisition" },
      { value: "legal / compliance", label: "Legal / Compliance" },
    ],
  },
];

const normalize = (value?: string) => value?.trim().toLowerCase();

export const getClassificationGroup = (value?: string) => {
  const normalized = normalize(value);
  if (!normalized) return undefined;
  return CLASSIFICATION_OPTIONS.find((option) => option.value === normalized);
};

export const getClassificationLabel = (value?: string) => {
  const normalized = normalize(value);
  if (!normalized) return "-";

  const group = CLASSIFICATION_OPTIONS.find(
    (option) =>
      option.value === normalized || option.label.toLowerCase() === normalized
  );

  return group?.label || value || "-";
};

export const getClassificationSummary = (classification?: ClassificationValue) => {
  if (!classification?.main) return "-";

  const mainLabel = getClassificationLabel(classification.main);
  const subLabels =
    classification?.sub
      ?.map((sub) => {
        const normalizedSub = normalize(sub);
        const group = getClassificationGroup(classification.main);
        const match = group?.subOptions?.find(
          (option) =>
            option.value === normalizedSub ||
            option.label.toLowerCase() === normalizedSub
        );
        return match?.label || sub;
      })
      .filter(Boolean) || [];

  if (!subLabels.length) return mainLabel;
  return `${mainLabel} - ${subLabels.join(", ")}`;
};

